import { useMemo, useState } from 'react';

import { Modal } from '../../../../components/Modal/Modal';
import {
  BULK_STATUS_TARGETS,
  notifiesCustomer,
  planBulkStatusChange,
  statusLabel,
} from '../../../../components/OrderBulkActionsBar/bulkStatus';
import type { BulkAction, BulkActionBarContext } from '../types';

// Move every eligible selected load to a target status. Approvals route through
// the approve endpoint (full body); the plan previews what's skipped and why.
function SetStatusTrigger({ ctx }: { ctx: BulkActionBarContext }) {
  const { rows, count, onApplyStatus, isApplyingStatus } = ctx;
  const [targetStatus, setTargetStatus] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const plan = useMemo(() => planBulkStatusChange(rows, targetStatus), [rows, targetStatus]);

  const skipped =
    plan.alreadyInStatus.length +
    plan.notAllowed.length +
    plan.missingApprovalDetails.length +
    plan.missingPickupDate.length;

  const apply = async () => {
    await onApplyStatus(targetStatus, plan.eligible);
    setConfirmOpen(false);
    setTargetStatus('');
  };

  return (
    <>
      <div className="bulk-bar__group">
        <label className="bulk-bar__label" htmlFor="bulk-status">
          Set status
        </label>

        <select
          id="bulk-status"
          value={targetStatus}
          onChange={(event) => setTargetStatus(event.target.value)}
          disabled={isApplyingStatus}
        >
          <option value="">Choose…</option>

          {BULK_STATUS_TARGETS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => setConfirmOpen(true)}
          disabled={!targetStatus || isApplyingStatus}
        >
          {isApplyingStatus ? 'Applying…' : 'Apply'}
        </button>
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={`Set ${plan.eligible.length} load${
          plan.eligible.length === 1 ? '' : 's'
        } to ${statusLabel(targetStatus)}?`}
        size="sm"
        footer={
          <>
            <button
              className="btn btn--ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={isApplyingStatus}
            >
              Cancel
            </button>

            <button
              className="btn btn--primary"
              onClick={apply}
              disabled={isApplyingStatus || plan.eligible.length === 0}
            >
              {isApplyingStatus
                ? 'Applying…'
                : `Set ${plan.eligible.length} to ${statusLabel(targetStatus)}`}
            </button>
          </>
        }
      >
        {plan.eligible.length === 0 ? (
          <p>
            None of the {count} selected load{count === 1 ? '' : 's'} can move to{' '}
            <strong>{statusLabel(targetStatus)}</strong>. Pick a different status or adjust your
            selection.
          </p>
        ) : (
          <p>
            <strong>{plan.eligible.length}</strong> of {count} selected load
            {count === 1 ? '' : 's'} will change to <strong>{statusLabel(targetStatus)}</strong>.
          </p>
        )}

        {skipped > 0 && (
          <ul className="bulk-confirm__list">
            {plan.alreadyInStatus.length > 0 && (
              <li>
                {plan.alreadyInStatus.length} skipped (already {statusLabel(targetStatus)}).
              </li>
            )}

            {plan.notAllowed.length > 0 && (
              <li>
                {plan.notAllowed.length} can&apos;t be changed in bulk
                {targetStatus === 'APPROVED'
                  ? ' (already picked up, or on the waitlist — open the load to approve it).'
                  : ' (awaiting approval or already picked up).'}
              </li>
            )}

            {plan.missingApprovalDetails.length > 0 && (
              <li>
                {plan.missingApprovalDetails.length} need a warehouse, fulfillment method or pallet
                size before they can be approved — open each load to finish it.
              </li>
            )}

            {plan.missingPickupDate.length > 0 && (
              <li>
                {plan.missingPickupDate.length} need a pickup date before they can be marked ready
                for pickup — open each load to set one.
              </li>
            )}
          </ul>
        )}

        {plan.eligible.length > 0 && notifiesCustomer(targetStatus) && (
          <div className="bulk-confirm__warning">
            Customers with this notification enabled will be emailed about each of the{' '}
            {plan.eligible.length} change
            {plan.eligible.length === 1 ? '' : 's'}.
          </div>
        )}
      </Modal>
    </>
  );
}

export const SetStatusAction: BulkAction = {
  id: 'set-status',
  isAvailable: () => true,
  Component: SetStatusTrigger,
};
